import { type z } from 'zod';
import { type CanonicalModel, indexCanonicalObjects, SYSTEM_OBJECT_ID } from './canonical';
import {
  attributeConfidence,
  deriveConfidence,
  objectConfidence,
  predicateBasisFromProvenance,
} from './confidence';
import {
  type CanonicalObjectType,
  type PredicateBasis,
  PRIMARY_GROUNDING_TYPES,
  type ProseOrigin,
} from './enums';
import { evaluatePredicate, type PredicateVerdict } from './predicate';
import {
  type AttributeDependency,
  type CandidateFinding,
  CandidateFindingDraftSchema,
  type ConfidenceBasis,
} from './finding';
import { type IdFactory, newId } from './ids';
import type { ThreatPatternIndex } from './pack';
import { type Clock, nowIso } from './primitives';
import { establishedAttributes, type Provenanced, provenanceOfAttribute } from './provenance';
import {
  assetCriticality,
  type RiskDimensions,
  type Scorer,
  strongerDimension,
  unestablishedRiskDimensions,
  unscoredScorer,
} from './scoring';

/**
 * Grounding by schema (CLAUDE.md principle 4, DESIGN_SPEC §7.1, §11.1).
 *
 * Every candidate finding must reference at least one real canonical object id
 * and a real pack `threat_pattern_id`. Findings that cite ids which do not exist
 * are rejected **before review** — enforced here, in code, not by asking a model
 * to check its own work and not by a reviewer noticing.
 *
 * Rejection is not deletion. The caller records rejected drafts against the
 * agent run (status `rejected`, with `rejection_reason`), which is what makes
 * grounding failures visible and measurable rather than silent.
 */

export const FINDING_REJECTION_CODES = [
  /** Failed the draft schema — including a worker trying to self-report confidence. */
  'schema_invalid',
  /** The model handed in does not belong to the tenant/assessment being validated. */
  'model_scope_mismatch',
  /** `threat_pattern_id` does not resolve in any pinned pack. */
  'unknown_threat_pattern',
  /** The finding's domain contradicts the pack the pattern came from. */
  'domain_pack_mismatch',
  /** A grounded id does not resolve to an object in the canonical model. */
  'ungrounded_object_id',
  /** The same object cited twice — ambiguous, and it would skew the reproducibility key. */
  'duplicate_grounding',
  /** Grounded only on actors, environments or controls; no architecture object. */
  'no_primary_grounding',
  /**
   * The pattern requires object types **the canonical model does not contain**.
   *
   * Corrected in sds-04. This checked that the *finding grounded on* every
   * required type, which misreads what the field is: DESIGN_SPEC §8.3 step 1
   * makes `requires_object_types` the pre-selection filter — the types that must
   * be *present* for the pattern to be a candidate at all. The two coincided
   * until a pattern needed a type it does not cite: "traffic crosses a boundary
   * with no control on the crossing" requires a trust boundary to exist, and
   * grounds on the flow. Nothing exercised the difference before, because no
   * pattern had one.
   *
   * Nothing is lost by the correction: `predicate_unsatisfied` already requires
   * a grounded object of the pattern's `subject_type` for which the predicate
   * holds, which is the property the old check was reaching for.
   */
  'pattern_requirements_unmet',
  /** Cited evidence that the canonical model does not list. */
  'ungrounded_evidence_ref',
  /**
   * A declared attribute dependency names an object **the canonical model does
   * not contain** (docs/DECISIONS.md ZD-020, corrected by ZD-029 §5). A caller
   * error rather than a worker error — the list comes from the pack service —
   * but rejected rather than dropped, because silently ignoring it would compute
   * confidence from fewer inputs than the caller believed and read as more
   * confident than it should.
   *
   * **Not restricted to the objects the finding grounds on**, which is what this
   * check demanded until sds-04. A negative claim legitimately depends on an
   * object the finding is not about: "nothing protects this database" rests on
   * every observed control's `applies_to`, and the finding grounds on the
   * database, not on the controls. Grounding on them instead would put every
   * control into every finding's `grounded_object_ids` and wreck the
   * reproducibility tuple. Widening is safe by construction: `deriveConfidence`
   * takes the **weakest** of its inputs, so an extra dependency can only lower
   * confidence or leave it unchanged — never raise it.
   */
  'attribute_dependency_ungrounded',
  /**
   * The pattern's own applicability predicate does not hold on the objects the
   * finding cites (docs/DECISIONS.md ZD-029 §6).
   *
   * Until the predicate was evaluable, a worker could anchor to any pattern and
   * ground on any object, and applicability was taken on trust — which is the
   * gap that would let an LLM contextualiser invent an application. `false` is
   * refused outright; `unknown` is permitted **only** on a finding that declares
   * itself conditional, which is what makes `conditional` load-bearing rather
   * than decorative.
   */
  'predicate_unsatisfied',
] as const;

export type FindingRejectionCode = (typeof FINDING_REJECTION_CODES)[number];

export interface FindingRejection {
  code: FindingRejectionCode;
  message: string;
  path?: (string | number)[];
  offending_ids?: string[];
}

export interface FindingValidationContext {
  tenant_id: string;
  assessment_id: string;
  model: CanonicalModel;
  /** Patterns from the packs pinned to this assessment (see `indexThreatPatterns`). */
  patterns: ThreatPatternIndex;
  /**
   * Whether this draft's prose is a transcript of its own witnesses or a
   * model's contextualisation (docs/DECISIONS.md ZD-031).
   *
   * Required, and taken from the run context rather than from the draft: the
   * draft schema is strict and has no field for it, so a worker cannot claim
   * its own prose is a transcript. Required rather than defaulted because a
   * default would be assumed at exactly the call site that most needs to say.
   */
  prose_origin: ProseOrigin;
  /** The agent run that produced the draft, for provenance. */
  run_id?: string | null;
  /** Defaults to `unscoredScorer`; sds-05 supplies QRS materiality. */
  score?: Scorer;
  newId?: IdFactory;
  clock?: Clock;
}

export interface ValidateFindingOptions {
  /**
   * How the pattern's applicability predicate was satisfied, from the pack
   * service's evaluation (docs/DECISIONS.md ZD-029). Omitted, it is inferred
   * conservatively from the grounded objects' provenance — which is what a
   * hand-created finding or an approver edit gets, since neither has an
   * evaluation behind it.
   */
  predicate_basis?: PredicateBasis;

  /**
   * The attributes this finding actually rests on (docs/DECISIONS.md ZD-020) —
   * the predicate's own **witnesses**, from `evaluatePredicate`.
   *
   * Never a list an author wrote beside the predicate: a declared list is a
   * second copy that nothing can check, and it drifts the first time a pattern
   * is edited (ZD-029 §1).
   *
   * **Omitted, every separately-established attribute of every grounded object
   * is assumed to matter.** That fallback is deliberately at least as
   * pessimistic as the behaviour it replaced, and it is still the right answer
   * for a caller with no evaluation behind it.
   */
  depends_on_attributes?: { object_id: string; attribute: string }[];

  /**
   * The assets this finding is about, from the statement's `impacted_assets`
   * slot (docs/DECISIONS.md ZD-058 §3).
   *
   * `asset_criticality` is the **only** dimension with a source in the client's
   * model rather than in the pack, so it is the only reason a severity band
   * varies between two systems the same pattern fires on. It is passed in
   * rather than re-derived because the slot builder already owns the rule, and
   * a second derivation here could disagree with the sentence a client reads —
   * the third-fact-that-can-disagree shape ZD-017 and ZD-027 rule 3 both refuse.
   *
   * **Omitted, the grounded objects are used**, which is the narrower rule and
   * is always a subset: a caller with no statement to hand still gets a
   * criticality where the predicate itself navigated to an asset, and never a
   * higher band than the statement would have produced.
   */
  impacted_asset_ids?: readonly string[];
}

export type FindingValidationResult =
  { ok: true; finding: CandidateFinding } | { ok: false; rejections: FindingRejection[] };

const PRIMARY_TYPES = new Set<CanonicalObjectType>(PRIMARY_GROUNDING_TYPES);

function fromZodError(error: z.ZodError): FindingRejection[] {
  return error.issues.map((issue) => ({
    code: 'schema_invalid' as const,
    message: issue.message,
    path: issue.path.filter((p): p is string | number => typeof p !== 'symbol'),
  }));
}

/**
 * Validates one worker-emitted draft into a persistable finding, or rejects it.
 *
 * Note what the caller cannot influence: `tenant_id`, `assessment_id` and
 * `finding_id` come from the context and the id factory, never from the draft.
 * A worker has no way to address another tenant's assessment, because the field
 * it would have to set does not exist on its output schema.
 */
export function validateFinding(
  input: unknown,
  ctx: FindingValidationContext,
  options: ValidateFindingOptions = {},
): FindingValidationResult {
  const rejections: FindingRejection[] = [];

  // The model must belong to the scope we were asked to validate against.
  // Checked first: every subsequent check reads ids out of this model, so a
  // mismatch here would mean grounding a finding against the wrong tenant.
  if (ctx.model.tenant_id !== ctx.tenant_id || ctx.model.assessment_id !== ctx.assessment_id) {
    return {
      ok: false,
      rejections: [
        {
          code: 'model_scope_mismatch',
          message:
            `canonical model is scoped to ${ctx.model.tenant_id}/${ctx.model.assessment_id}, ` +
            `but validation was requested for ${ctx.tenant_id}/${ctx.assessment_id}`,
        },
      ],
    };
  }

  const parsed = CandidateFindingDraftSchema.safeParse(input);
  if (!parsed.success) {
    return { ok: false, rejections: fromZodError(parsed.error) };
  }
  const draft = parsed.data;

  // --- Anchor: the pattern must exist in a pinned pack -----------------------
  const resolved = ctx.patterns.get(draft.threat_pattern_id);
  if (!resolved) {
    return {
      ok: false,
      rejections: [
        {
          code: 'unknown_threat_pattern',
          message: `threat_pattern_id ${draft.threat_pattern_id} is not in any pinned pack`,
          path: ['threat_pattern_id'],
          offending_ids: [draft.threat_pattern_id],
        },
      ],
    };
  }

  if (resolved.domain !== draft.domain) {
    rejections.push({
      code: 'domain_pack_mismatch',
      message: `finding claims domain ${draft.domain} but ${draft.threat_pattern_id} belongs to the ${resolved.domain} pack`,
      path: ['domain'],
    });
  }

  // stride_category, like analysis_lens below, is inherited from the resolved
  // pattern rather than taken from the draft — the draft schema has no field
  // for it, so there is nothing here to cross-check.

  // --- Grounding: every cited id must resolve in the canonical model ---------
  const objects = indexCanonicalObjects(ctx.model);

  const duplicates = draft.grounded_object_ids.filter(
    (id, i) => draft.grounded_object_ids.indexOf(id) !== i,
  );
  if (duplicates.length > 0) {
    rejections.push({
      code: 'duplicate_grounding',
      message: `grounded_object_ids contains duplicates: ${[...new Set(duplicates)].join(', ')}`,
      path: ['grounded_object_ids'],
      offending_ids: [...new Set(duplicates)],
    });
  }

  const unresolved = draft.grounded_object_ids.filter((id) => !objects.has(id));
  if (unresolved.length > 0) {
    rejections.push({
      code: 'ungrounded_object_id',
      message: `grounded_object_ids reference objects absent from the canonical model: ${unresolved.join(', ')}`,
      path: ['grounded_object_ids'],
      offending_ids: unresolved,
    });
  }

  const groundedTypes = new Set<CanonicalObjectType>();
  for (const id of draft.grounded_object_ids) {
    const entry = objects.get(id);
    if (entry) {
      groundedTypes.add(entry.type);
    }
  }

  if (unresolved.length === 0 && ![...groundedTypes].some((t) => PRIMARY_TYPES.has(t))) {
    rejections.push({
      code: 'no_primary_grounding',
      message:
        'a finding must ground on at least one component, asset, flow or trust boundary — ' +
        'actors, environments and observed controls do not ground a threat on their own',
      path: ['grounded_object_ids'],
    });
  }

  const presentTypes = new Set<CanonicalObjectType>();
  for (const entry of objects.values()) presentTypes.add(entry.type);
  const missingTypes = resolved.pattern.applicability.requires_object_types.filter(
    (t) => !presentTypes.has(t),
  );
  if (missingTypes.length > 0) {
    rejections.push({
      code: 'pattern_requirements_unmet',
      message: `${draft.threat_pattern_id} requires a model containing ${missingTypes.join(', ')}`,
    });
  }

  // --- Evidence must be evidence the model actually holds --------------------
  const modelEvidence = new Set(ctx.model.evidence_refs);
  const strayEvidence = draft.evidence_refs.filter((ref) => !modelEvidence.has(ref));
  if (strayEvidence.length > 0) {
    rejections.push({
      code: 'ungrounded_evidence_ref',
      message: `evidence_refs cite material not listed on the canonical model: ${strayEvidence.join(', ')}`,
      path: ['evidence_refs'],
      offending_ids: strayEvidence,
    });
  }

  // --- The pattern must actually apply to what the finding cites ------------
  // docs/DECISIONS.md ZD-029 §6. Evaluated against every grounded object of the
  // pattern's subject type — the finding claims the pattern applies here, so at
  // least one of the objects it cites has to be a subject the predicate does
  // not refuse. `pattern_requirements_unmet` above guarantees there is one.
  const VERDICT_RANK: Record<PredicateVerdict, number> = { false: 0, unknown: 1, true: 2 };
  let verdict: PredicateVerdict = 'false';
  if (unresolved.length === 0) {
    const evaluationContext = { model: ctx.model, objects };
    for (const id of draft.grounded_object_ids) {
      if (objects.get(id)?.type !== resolved.pattern.applicability.subject_type) continue;
      const outcome = evaluatePredicate(
        resolved.pattern.applicability.predicate,
        evaluationContext,
        id,
      );
      if (VERDICT_RANK[outcome.verdict] > VERDICT_RANK[verdict]) verdict = outcome.verdict;
    }

    if (verdict === 'false') {
      rejections.push({
        code: 'predicate_unsatisfied',
        message:
          `${draft.threat_pattern_id}'s applicability predicate does not hold on any ` +
          `${resolved.pattern.applicability.subject_type} this finding grounds on`,
        path: ['grounded_object_ids'],
      });
    } else if (verdict === 'unknown' && !draft.conditional.is_conditional) {
      rejections.push({
        code: 'predicate_unsatisfied',
        message:
          `${draft.threat_pattern_id}'s applicability predicate could not be decided on this ` +
          `model, so the finding must declare itself conditional rather than assert the threat`,
        path: ['conditional', 'is_conditional'],
      });
    }
  }

  if (rejections.length > 0) {
    return { ok: false, rejections };
  }

  // --- Derived confidence (§8.4) — computed, never asked of a model ----------
  const grounded = draft.grounded_object_ids.map((id) => ({ id, entry: objects.get(id)! }));

  // Which attributes this finding rests on (docs/DECISIONS.md ZD-020). Absent a
  // predicate evaluator, every attribute with its own provenance is assumed to
  // matter — an attribute with no entry inherits the object's provenance, which
  // `grounded_object_confidences` already accounts for, so this is exactly the
  // set that can differ from the object.
  const declared = options.depends_on_attributes;
  const dependencyPairs =
    declared ??
    grounded.flatMap(({ id, entry }) =>
      establishedAttributes(entry).map((attribute) => ({ object_id: id, attribute })),
    );

  if (declared) {
    // Anything the model holds, plus the system itself. The system is readable
    // but not groundable (docs/DECISIONS.md ZD-029 §7) — it is the subject of
    // the assessment rather than an object in it, so it is absent from
    // `indexCanonicalObjects` and cannot be cited in `grounded_object_ids`, but
    // `system.multi_tenant` is a real predicate and dropping it would compute
    // confidence from fewer inputs than the predicate used.
    const known = new Set([...objects.keys(), SYSTEM_OBJECT_ID]);
    const stray = declared.filter((d) => !known.has(d.object_id));
    if (stray.length > 0) {
      return {
        ok: false,
        rejections: [
          {
            code: 'attribute_dependency_ungrounded',
            message: `declared attribute dependencies name objects absent from the canonical model: ${stray
              .map((d) => `${d.object_id}.${d.attribute}`)
              .join(', ')}`,
            offending_ids: stray.map((d) => d.object_id),
          },
        ],
      };
    }
  }

  /** Resolves a dependency's owner, which may be the system itself (ZD-029 §7). */
  const provenancedOf = (id: string): Provenanced =>
    id === SYSTEM_OBJECT_ID ? ctx.model.system : objects.get(id)!;

  const dependencies: AttributeDependency[] = dependencyPairs.map(({ object_id, attribute }) => ({
    object_id,
    attribute,
    confidence: attributeConfidence(provenancedOf(object_id), attribute),
  }));

  // Assumptions reachable from the object *or* from any attribute it rests on:
  // a fact derived from an assumption is exactly the case ZD-020 made
  // expressible, and missing it here would put the finding's confidence back
  // above what its inputs support.
  const assumptionIds = [
    ...new Set([
      ...grounded.flatMap(({ entry }) => entry.provenance.derived_from_assumption_ids),
      ...dependencies.flatMap(
        ({ object_id, attribute }) =>
          provenanceOfAttribute(provenancedOf(object_id), attribute).derived_from_assumption_ids,
      ),
    ]),
  ].sort();

  const groundedSet = new Set(draft.grounded_object_ids);
  const unknownIds = ctx.model.unknowns
    .filter(
      (u) =>
        u.resolved_by_answer_id === null && u.related_object_ids.some((id) => groundedSet.has(id)),
    )
    .map((u) => u.unknown_id)
    .sort();

  const confidence_basis: ConfidenceBasis = {
    predicate_basis:
      options.predicate_basis ??
      predicateBasisFromProvenance([
        ...grounded.map(({ entry }) => entry.provenance.basis),
        ...dependencies.map(
          ({ object_id, attribute }) =>
            provenanceOfAttribute(provenancedOf(object_id), attribute).basis,
        ),
      ]),
    grounded_object_confidences: grounded.map(({ entry }) => objectConfidence(entry.provenance)),
    depends_on_attributes: dependencies,
    depends_on_assumption_ids: assumptionIds,
    depends_on_unknown_ids: unknownIds,
  };

  const confidence = deriveConfidence(confidence_basis);

  /*
   * The pattern's typed defaults (docs/DECISIONS.md ZD-011) overlaid on
   * all-unestablished, then the one dimension the **client's** model can speak
   * to (ZD-058 §3).
   *
   * The base is `unestablished` rather than `low`, which is the ZD-058 §2
   * correction: a dimension no pattern states and no model implies used to
   * arrive at the scorer as a band, and `low` is a claim — *this rarely
   * happens*, *the loss would be small* — that nobody made. Measured, that was
   * `control_strength` and `secondary_loss_magnitude` on **every finding in the
   * system**, since 0 of 65 patterns state either.
   */
  const dimensions: RiskDimensions = {
    ...unestablishedRiskDimensions(confidence),
    ...Object.fromEntries(
      Object.entries(resolved.pattern.default_scoring_inputs).filter(([, v]) => v !== undefined),
    ),
  };
  dimensions.asset_criticality = strongerDimension(
    dimensions.asset_criticality,
    assetCriticality(ctx.model, options.impacted_asset_ids ?? draft.grounded_object_ids),
  );

  const score = (ctx.score ?? unscoredScorer)({
    draft,
    pattern: resolved,
    model: ctx.model,
    confidence,
    dimensions,
  });

  const mint = ctx.newId ?? newId;
  const at = (ctx.clock ?? nowIso)();

  return {
    ok: true,
    finding: {
      finding_id: mint('finding'),
      tenant_id: ctx.tenant_id,
      assessment_id: ctx.assessment_id,
      run_id: ctx.run_id ?? null,
      state: 'candidate',
      domain: draft.domain,
      analysis_lens: resolved.pattern.analysis_lens,
      stride_category: resolved.pattern.stride_category,
      title: draft.title,
      description: draft.description,
      rationale: draft.rationale,
      prose_origin: ctx.prose_origin,
      threat_pattern_id: draft.threat_pattern_id,
      pack_id: resolved.pack_id,
      pack_version: resolved.pack_version,
      grounded_object_ids: draft.grounded_object_ids,
      confidence,
      confidence_basis,
      conditional: draft.conditional,
      severity_normalised: score.severity_normalised,
      scoring_method: score.scoring_method,
      scoring_detail: score.scoring_detail,
      risk_dimensions: score.dimensions,
      evidence_refs: draft.evidence_refs,
      created_at: at,
      updated_at: at,
    },
  };
}

export interface BatchValidationResult {
  accepted: CandidateFinding[];
  rejected: { input: unknown; rejections: FindingRejection[] }[];
}

/**
 * Validates a whole worker output. Rejected drafts are returned rather than
 * thrown away: DESIGN_SPEC §11.1 wants grounding failures counted, and the eval
 * harness in sds-09 measures them.
 */
export function validateFindings(
  inputs: readonly unknown[],
  ctx: FindingValidationContext,
  options: ValidateFindingOptions = {},
): BatchValidationResult {
  const accepted: CandidateFinding[] = [];
  const rejected: { input: unknown; rejections: FindingRejection[] }[] = [];

  for (const input of inputs) {
    const result = validateFinding(input, ctx, options);
    if (result.ok) {
      accepted.push(result.finding);
    } else {
      rejected.push({ input, rejections: result.rejections });
    }
  }

  return { accepted, rejected };
}
